import React, { useState, useEffect } from 'react';
import './animate.css';
import { useDispatch, useSelector } from 'react-redux';
import { gradeApi } from '../../../../api.js';
import { useAuth } from '../../../../auth/AuthContext';
import { useQueryParams } from '../../../../shared/hooks/use-query-params.tsx';
import { addStudents } from '../../../../store/studentSlice.js';
import { getStudents, removeStudent } from '../../../../store/teacherSlice.js';
import { DEFAULT_PAGE_NUMBER, ITEMS_PER_PAGE } from '../../../../utils/pagination.ts';
import ConfirmationModal from '../../../ConfirmationModal';
import DragAndDropUpload from '../../../DragDropCSV';
import Table from '../../../Table.js';
import EditableMatchKeyCell from '../../EditableMatchKeyCell.js';
import EditableNicknameCell from '../../EditableNicknameCell.js';
import { Plus, X, Upload, Info, User, ChevronDown, ChevronUp, Loader } from 'lucide-react';
import { FiMousePointer } from 'react-icons/fi';

const AddDropBig = ({ course, class_id, onRosterImported, rosterImported }) => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [csv, setCsv] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const { data, isLoading, errorMessage } = useSelector(state => state?.teacherReducer?.students);

  const [isAddStudentSectionOpen, setIsAddStudentSectionOpen] = useState(false);
  const [studentErrors, setStudentErrors] = useState(null);
  const [isBulkImportSectionOpen, setIsBulkImportSectionOpen] = useState(false);
  const [studentEmail, setStudentEmail] = useState('');

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [expandedImportTab, setExpandedImportTab] = useState(null); // 'google' or 'canvas'
  const [cursorPosition, setCursorPosition] = useState('google'); // Start with cursor at google
  console.log('data', data);
  const {
    params: { search, pageNumber, itemsPerPage, sortOrder },
    updateSearchParams
  } = useQueryParams({
    search: '',
    pageNumber: DEFAULT_PAGE_NUMBER,
    itemsPerPage: ITEMS_PER_PAGE.default,
    sortOrder: 'asc'
  });

  const isOpenToManage = true;

  // Check if the only student is the test student
  const hasOnlyTestStudent = () => {
    if (!data?.entities || data.entities.length !== 1) {
      return false;
    }
    return data.entities[0].email === 'student@test.com';
  };

  // This useEffect will toggle the cursor position between the two buttons
  useEffect(() => {
    // Only animate if there are no students OR only test student AND no import tab is expanded
    if ((data?.totalCount === 0 || hasOnlyTestStudent()) && expandedImportTab === null) {
      const interval = setInterval(() => {
        setCursorPosition(prev => (prev === 'google' ? 'canvas' : 'google'));
      }, 3000); // Switch between buttons every 3 seconds

      return () => clearInterval(interval);
    }
  }, [data, expandedImportTab]);

  // Add the CSS keyframes for the animations
  useEffect(() => {
    // Add the keyframes for the glow animation if they don't exist
    if (!document.getElementById('cursor-animation-keyframes')) {
      const style = document.createElement('style');
      style.id = 'cursor-animation-keyframes';
      style.innerHTML = `
        @keyframes button-glow {
          0% { box-shadow: 0 0 10px rgba(228, 177, 122, 0.4); }
          50% { box-shadow: 0 0 20px rgba(228, 177, 122, 0.7); }
          100% { box-shadow: 0 0 10px rgba(228, 177, 122, 0.4); }
        }
        
        @keyframes click-ripple {
          0% { transform: scale(0.1); opacity: 0.7; }
          50% { opacity: 0.5; }
          100% { transform: scale(1); opacity: 0; }
        }
        
        .click-ripple {
          background-color: rgba(228, 177, 122, 0.7);
          border-radius: 50%;
          width: 100%;
          height: 100%;
          animation: click-ripple 2s ease-out infinite;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    getAllStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, pageNumber, itemsPerPage, sortOrder]);

  useEffect(() => {
    if (errorMessage) {
      setError(errorMessage);
    }
  }, [errorMessage]);

  const getAllStudents = () =>
    dispatch(
      getStudents({
        classId: class_id,
        token: user.session_token,
        search,
        pageNumber: Number(pageNumber),
        itemsPerPage: Number(itemsPerPage),
        sortOrder
      })
    );

  const handleFileChange = file => {
    if (file) {
      setCsv(file);
      setError(''); // Clear any previous errors
    } else {
      setError('No file selected or invalid file type.');
    }
  };

  const handleUpload = async () => {
    if (!csv) {
      setError('Please select a CSV file first.');
      return;
    }

    // Clear previous errors and set uploading state
    setError('');
    setSuccess('');
    setIsUploading(true);

    // Parse CSV headers
    try {
      const reader = new FileReader();
      reader.onload = async e => {
        try {
          const text = e.target.result;
          const lines = text.split('\n');
          if (lines.length <= 1) {
            setError('CSV file appears to be empty or invalid.');
            setIsUploading(false);
            return;
          }

          // Get headers from first line, clean them, and normalize to lowercase
          const headers = lines[0].split(',').map(header => header.trim().toLowerCase());

          // Process based on selected import tab
          if (expandedImportTab === 'google') {
            // Check if expected Google Classroom headers exist
            const emailIndex = headers.findIndex(h => h.toLowerCase() === 'email address');
            const firstNameIndex = headers.findIndex(h => h.toLowerCase() === 'first name');
            const lastNameIndex = headers.findIndex(h => h.toLowerCase() === 'last name');

            if (emailIndex === -1 || firstNameIndex === -1 || lastNameIndex === -1) {
              // Missing expected headers
              let missingColumns = [];
              if (emailIndex === -1) {
                missingColumns.push('email address');
              }
              if (firstNameIndex === -1) {
                missingColumns.push('first name');
              }
              if (lastNameIndex === -1) {
                missingColumns.push('last name');
              }
              setError(`Invalid Google Classroom CSV format. Missing required columns: ${missingColumns.join(', ')}.

Please make sure you've exported the roster correctly from Google Classroom.`);
              setIsUploading(false);
              return;
            }

            // All required Google Classroom headers found - proceed with automatic mapping
            const type = 'google';

            await processImport(type, csv);
          } else if (expandedImportTab === 'canvas') {
            // Check if expected Google Classroom headers exist
            const emailIndex = headers.findIndex(h => h === 'email');
            const nameIndex = headers.findIndex(h => h === 'sortable name');

            if (emailIndex === -1 || nameIndex === -1) {
              // Missing expected headers
              let missingColumns = [];
              if (emailIndex === -1) {
                missingColumns.push('email address');
              }
              if (nameIndex === -1) {
                missingColumns.push('first name');
              }
              setError(`Invalid Canvas CSV format. Missing required columns: ${missingColumns.join(', ')}.

Please make sure you've exported the roster correctly from Canvas.`);
              setIsUploading(false);
              return;
            }

            // All required Google Classroom headers found - proceed with automatic mapping
            const type = 'canvas';
            await processImport(type, csv);
          }
          // If this is a Canvas import, automatically set the column mappings
          else {
            setError(
              'Please select either Google Classroom or Canvas import tab before uploading.'
            );
            setIsUploading(false);
          }
        } catch (err) {
          console.error('Error parsing CSV:', err);
          setError(`Failed to parse CSV file: ${err.message}. Please check the file format.`);
          setIsUploading(false);
        }
      };

      reader.onerror = () => {
        setError('Failed to read the uploaded file. Please try again with a different file.');
        setIsUploading(false);
      };

      reader.readAsText(csv);
    } catch (error) {
      console.error('Error reading CSV:', error);
      setError(`Failed to read CSV file: ${error.message}`);
      setIsUploading(false);
      setCsv(null); // Clear the uploaded file
      setExpandedImportTab(null); // Reset the selected import tab
      setIsBulkImportSectionOpen(false); // Close the import section after completion
    }
  };

  // Helper function to process the import with the specified mappings
  const processImport = async (type, csvFile) => {
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('classId', class_id);
      formData.append('type', type);

      const response = await gradeApi.post({
        path: '/api/upload-csv/',
        body: formData,
        config: {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      });

      const { success_count, failed_count, failed_emails } = response.data;

      // Always call getAllStudents() after successful upload
      await getAllStudents(); // Use await to ensure it completes before continuing

      // Clear previous messages to avoid showing both
      setError('');
      setSuccess('');

      // Show a single combined message that represents the actual result
      if (success_count > 0 && failed_count > 0) {
        // If we have both success and failures, show a combined message
        setSuccess(`Added ${success_count} student(s). Failed to add ${failed_count} student(s).`);
        onRosterImported(true);

        // If there are specific emails that failed, show them in a more subtle way
        if (failed_emails && failed_emails.length > 0) {
          console.log('Failed emails:', failed_emails.join(', '));
        }
      } else if (failed_count > 0) {
        // If only failures, show error
        setError(`Failed to add ${failed_count} student(s): ${failed_emails.join(', ')}`);
      } else {
        // If only successes, show success
        setSuccess(`Successfully added ${success_count} student(s)`);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setSuccess(''); // Clear any success message first
      setError(
        `Failed to process the CSV file: ${error.message || 'Unknown error'}. Please try again.`
      );
    } finally {
      setIsUploading(false);
      setCsv(null); // Clear the uploaded file
      setExpandedImportTab(null); // Reset the selected import tab
      setIsBulkImportSectionOpen(false); // Close the import section after completion
    }
  };

  const handleMatchKeyConfirm = async mappings => {
    setIsMatchKeyModalOpen(false);
    setIsUploading(true);
    setError('');

    const formData = new FormData();
    formData.append('file', csv);
    formData.append('classId', class_id);
    formData.append('emailColumn', mappings.emailColumn);
    formData.append('hasNameColumns', String(mappings.hasNameColumns));
    if (mappings.hasNameColumns) {
      formData.append('firstNameColumn', mappings.firstNameColumn);
      formData.append('lastNameColumn', mappings.lastNameColumn);
    }
    formData.append('hasStudentId', String(mappings.hasStudentId));
    if (mappings.hasStudentId) {
      formData.append('studentIdColumn', mappings.studentIdColumn);
    }

    try {
      const response = await gradeApi.post({
        path: '/api/upload-csv/',
        body: formData,
        config: {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      });

      const { success_count, failed_count, failed_emails } = response.data;

      // Always call getAllStudents() after successful upload
      await getAllStudents(); // Use await to ensure it completes before continuing

      // Clear previous messages to avoid showing both
      setError('');
      setSuccess('');

      // Show a single combined message that represents the actual result
      if (success_count > 0 && failed_count > 0) {
        // If we have both success and failures, show a combined message
        onRosterImported(true);
        setSuccess(`Added ${success_count} student(s). Failed to add ${failed_count} student(s).`);

        // If there are specific emails that failed, show them in a more subtle way
        if (failed_emails && failed_emails.length > 0) {
          console.log('Failed emails:', failed_emails.join(', '));
        }
      } else if (failed_count > 0) {
        // If only failures, show error
        setError(`Failed to add ${failed_count} student(s): ${failed_emails.join(', ')}`);
      } else {
        // If only successes, show success
        setSuccess(`Successfully added ${success_count} student(s)`);
        onRosterImported(true);
        // Notify parent component that roster was imported
        if (success_count > 0 && onRosterImported) {
          onRosterImported();
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setSuccess(''); // Clear any success message first
      setError('Failed to process the CSV file. Please try again.');
    } finally {
      setIsUploading(false);
      setCsv(null); // Clear the uploaded file
      setExpandedImportTab(null); // Reset the selected import tab
      setIsBulkImportSectionOpen(false); // Close the import section after completion
    }
  };

  const handleRemove = async id => {
    setSelectedStudentId(id);
    setIsConfirmationModalOpen(true);
  };

  const handleMatchKeyUpdate = async (studentId, newMatchKey) => {
    try {
      await gradeApi.patch({
        path: `/api/courses/${class_id}/students/${studentId}/match-key`,
        body: { match_key: newMatchKey },
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });
      getAllStudents(); // Refresh the table data
      setSuccess('Student ID updated successfully');
    } catch (error) {
      console.error('Error updating match key:', error);
      setError('Failed to update student ID');
      throw error;
    }
  };

  const handleNicknameUpdate = async (studentId, nickFirstName, nickLastName) => {
    try {
      await gradeApi.patch({
        path: `/api/courses/${class_id}/students/${studentId}/nicknames`,
        body: {
          nick_first_name: nickFirstName,
          nick_last_name: nickLastName
        },
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });
      getAllStudents(); // Refresh the table data
      setSuccess('Student nickname updated successfully');
    } catch (error) {
      console.error('Error updating nickname:', error);
      setError('Failed to update student nickname');
      throw error;
    }
  };

  const addStudentByEmail = async () => {
    if (!studentEmail) {
      setError('Please enter a student email');
      return;
    }

    try {
      // Clear previous messages
      setSuccess('');
      setError('');
      setStudentErrors(null);

      const response = await dispatch(
        addStudents({
          classId: class_id,
          token: user.session_token,
          emails: [studentEmail]
        })
      );

      // Check if we got a response with payload
      if (response.payload) {
        // Check if there were any issues
        const hasIssues =
          response.payload?.already_added_emails?.length > 0 ||
          response.payload?.not_a_student_emails?.length > 0 ||
          response.payload?.invalid_emails?.length > 0 ||
          response.payload?.teacher_not_found_emails?.length > 0;

        if (hasIssues) {
          // Store the specific errors
          setStudentErrors(response.payload);
        } else {
          // Everything went well
          setSuccess('Student added successfully');
          setIsAddStudentSectionOpen(false);
          setStudentEmail('');
        }
      } else if (response.error) {
        // There was an error in the response
        setError('Failed to add student. Please check the email and try again.');
      } else {
        // Success case
        setSuccess('Student added successfully');
        setIsAddStudentSectionOpen(false);
        setStudentEmail('');
      }

      // Always refresh the student list
      await getAllStudents();
    } catch (error) {
      console.error('Error adding student:', error);
      setError('Failed to add student. Please check the email and try again.');
      setSuccess('');
    }
  };

  const confirmRemoveStudent = async () => {
    const deleteUser = await dispatch(
      removeStudent({
        classId: class_id,
        token: user.session_token,
        studentId: selectedStudentId
      })
    );

    if (deleteUser.error) {
      setError(deleteUser.payload);
    } else {
      setSuccess('Student removed successfully');
      getAllStudents();
    }

    setIsConfirmationModalOpen(false);
  };

  const columnNames = ['Name', 'Email', 'Student ID', 'Nickname', 'Actions'];

  return (
    <div className="students-management-container">
      {/* Feedback messages */}
      {success && (
        <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md border border-green-200 flex justify-between items-center">
          <span>{success}</span>
          <button className="text-green-700" onClick={() => setSuccess('')}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md border border-red-200 flex justify-between items-center">
          <span>{error}</span>
          <button className="text-red-700" onClick={() => setError('')}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="mb-6">
        <h4 className="font-medium text-gray-800 mb-4 text-lg">Click to select your LMS</h4>

        {/* Side-by-side Import Options */}
        <div className="flex space-x-4 mb-6 relative">
          {/* Google Classroom Option */}
          <div
            className={`w-1/2 p-6 flex flex-col items-center justify-center rounded-lg cursor-pointer transition-all duration-200 border-2 ${
              expandedImportTab === 'google'
                ? 'border-[#e4b17a] bg-orange-50'
                : 'border-[#e4b17a] hover:bg-orange-50'
            } ${data?.totalCount === 0 || hasOnlyTestStudent() ? 'import-button-glow' : ''}`}
            style={{
              ...((data?.totalCount === 0 || hasOnlyTestStudent()) && {
                animation: 'button-glow 2.5s infinite',
                position: 'relative',
                zIndex: 10
              })
            }}
            onClick={() => setExpandedImportTab(expandedImportTab === 'google' ? null : 'google')}
          >
            <span className="font-medium text-gray-800">Google Classroom Import</span>
          </div>

          {/* Canvas Option */}
          <div
            className={`w-1/2 p-6 flex flex-col items-center justify-center rounded-lg cursor-pointer transition-all duration-200 border-2 ${
              expandedImportTab === 'canvas'
                ? 'border-[#e4b17a] bg-orange-50'
                : 'border-[#e4b17a] hover:bg-orange-50'
            } ${data?.totalCount === 0 || hasOnlyTestStudent() ? 'import-button-glow' : ''}`}
            style={{
              ...((data?.totalCount === 0 || hasOnlyTestStudent()) && {
                animation: 'button-glow 2.5s infinite',
                position: 'relative',
                zIndex: 10
              })
            }}
            onClick={() => setExpandedImportTab(expandedImportTab === 'canvas' ? null : 'canvas')}
          >
            <span className="font-medium text-gray-800">Canvas LMS Import</span>
          </div>

          {/* Animated cursor that moves between the buttons */}
          {(data?.totalCount === 0 || hasOnlyTestStudent()) && expandedImportTab === null && (
            <div
              className="cursor-animation-container"
              style={{
                position: 'absolute',
                top: '80%',
                left: cursorPosition === 'google' ? '25%' : '75%',
                transform: 'translate(-50%, -50%)',
                transition: 'left 1s ease-in-out',
                zIndex: 20,
                pointerEvents: 'none'
              }}
            >
              <div className="pointer-container" style={{ position: 'relative' }}>
                <FiMousePointer
                  className="mouse-pointer"
                  size={24}
                  color="#e4b17a"
                  style={{
                    filter: 'drop-shadow(0 0 3px rgba(228, 177, 122, 0.7))',
                    transform: 'rotate(-5deg)'
                  }}
                />
              </div>

              {/* Ripple effect directly over the button */}
              <div
                className="click-ripple-container"
                style={{
                  position: 'absolute',
                  width: '48px',
                  height: '48px',
                  opacity: 0.9,
                  top: '-12px',
                  left: '8px'
                }}
              >
                <div className="click-ripple"></div>
              </div>
            </div>
          )}
        </div>
        {/* Expanded Import Sections */}
        {expandedImportTab === 'google' && (
          <div className="p-6 border border-gray-300 rounded-lg bg-white mb-4 animate-fadeIn relative">
            {isUploading && (
              <div className="processing-overlay">
                <Loader className="w-16 h-16 text-[#e4b17a] animate-spin" />
                <div className="processing-text">PROCESSING YOUR ROSTER</div>
              </div>
            )}
            <div className="flex flex-col space-y-4 mb-6">
              <h3 className="text-lg font-medium text-gray-800">Google Classroom Import</h3>
              <a
                href="https://docs.google.com/document/d/1okWxG38ghfbN7tAFuMm1mGq3mrkqYgk1BgYT3vc2RpM/edit?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center py-3 px-4 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 font-medium text-base hover:bg-gray-100 transition-colors"
              >
                <Info size={20} className="mr-2" /> View Instructions: How to Export Google
                Classroom Roster
              </a>
            </div>
            <div className="p-0 mb-4 rounded-lg border-2 border-dashed border-[#e4b17a] bg-orange-50 overflow-hidden">
              <DragAndDropUpload onFileChange={handleFileChange} />
            </div>
            <div className="flex justify-end">
              <button
                disabled={isUploading || !csv}
                className={`${isUploading ? 'px-8 py-4' : 'px-6 py-3'} rounded-md flex items-center gap-2 text-base font-medium transition-all duration-300 ${
                  !csv
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : isUploading
                      ? 'border-2 border-[#e4b17a] bg-gradient-to-r from-[#e4b17a] to-[#d9a064] text-white animate-pulse shadow-lg'
                      : 'border border-[#e4b17a] bg-[#e4b17a] text-white hover:bg-[#d9a064]'
                }`}
                onClick={handleUpload}
              >
                {isUploading ? (
                  <div className="flex items-center justify-center w-full gap-3">
                    <span className="text-lg font-bold animate-pulse">PROCESSING</span>
                    <Loader className="w-6 h-6 animate-spin" />
                  </div>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Start Import
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {expandedImportTab === 'canvas' && (
          <div className="p-6 border border-gray-300 rounded-lg bg-white mb-4 animate-fadeIn relative">
            {isUploading && (
              <div className="processing-overlay">
                <Loader className="w-16 h-16 text-[#e4b17a] animate-spin" />
                <div className="processing-text">PROCESSING YOUR ROSTER</div>
              </div>
            )}
            <div className="flex flex-col space-y-4 mb-6">
              <h3 className="text-lg font-medium text-gray-800">Canvas Import</h3>
              <a
                href="https://athelp.sfsu.edu/hc/en-us/articles/14014800586387-Download-a-list-of-student-email-addresses-in-Canvas"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center py-3 px-4 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 font-medium text-base hover:bg-gray-100 transition-colors"
              >
                <Info size={20} className="mr-2" /> View Instructions: How to Export Canvas Roster
              </a>
            </div>
            <div className="p-0 mb-4 rounded-lg border-2 border-dashed border-[#e4b17a] bg-orange-50 overflow-hidden">
              <DragAndDropUpload onFileChange={handleFileChange} />
            </div>
            <div className="flex justify-end">
              <button
                disabled={isUploading || !csv}
                className={`${isUploading ? 'px-8 py-4' : 'px-6 py-3'} rounded-md flex items-center gap-2 text-base font-medium transition-all duration-300 ${
                  !csv
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : isUploading
                      ? 'border-2 border-[#e4b17a] bg-gradient-to-r from-[#e4b17a] to-[#d9a064] text-white animate-pulse shadow-lg'
                      : 'border border-[#e4b17a] bg-[#e4b17a] text-white hover:bg-[#d9a064]'
                }`}
                onClick={handleUpload}
              >
                {isUploading ? (
                  <div className="flex items-center justify-center w-full gap-3">
                    <span className="text-lg font-bold animate-pulse">PROCESSING</span>
                    <Loader className="w-6 h-6 animate-spin" />
                  </div>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Upload CSV
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="mb-6 bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-6">
          <div className="space-y-4">
            <div>
              <label
                htmlFor="studentEmail"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Add Student by Email
              </label>
              <form
                className="relative"
                onSubmit={e => {
                  e.preventDefault();
                  addStudentByEmail();
                }}
              >
                <input
                  type="email"
                  id="studentEmail"
                  value={studentEmail}
                  placeholder="student@example.com"
                  className="w-full px-4 py-2 pr-[120px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#e4b17a] focus:border-[#e4b17a]"
                  onChange={e => setStudentEmail(e.target.value)}
                />
                <button
                  type="submit"
                  className="absolute right-1 top-1 px-3 py-1 border border-[#e4b17a] bg-[#e4b17a] text-white rounded-md hover:bg-[#d9a064] transition-colors flex items-center gap-1 text-sm"
                >
                  {isLoading ? (
                    <>
                      <span>Processing</span>
                      <Loader className="w-3 h-3 animate-spin" />
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Add Student
                    </>
                  )}
                </button>
              </form>
            </div>

            {studentErrors?.already_added_emails?.length > 0 && (
              <div className="text-red-600 text-sm">
                <p className="font-medium">Email already added:</p>
                <ul className="list-disc list-inside">
                  {studentErrors.already_added_emails.map(email => (
                    <li key={email}>{email}</li>
                  ))}
                </ul>
              </div>
            )}

            {studentErrors?.not_a_student_emails?.length > 0 && (
              <div className="text-red-600 text-sm">
                <p className="font-medium">Cannot add teacher as student:</p>
                <ul className="list-disc list-inside">
                  {studentErrors.not_a_student_emails.map(email => (
                    <li key={email}>{email}</li>
                  ))}
                </ul>
              </div>
            )}

            {studentErrors?.invalid_emails?.length > 0 && (
              <div className="text-red-600 text-sm">
                <p className="font-medium">Email not found:</p>
                <ul className="list-disc list-inside">
                  {studentErrors.invalid_emails.map(email => (
                    <li key={email}>{email}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Students Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mt-6">
        <Table
          search={search}
          sortOrder={sortOrder}
          isLoading={isLoading}
          totalCount={data?.totalCount}
          offset={data?.offset}
          nextOffset={data?.nextOffset}
          pageNumber={Number(pageNumber)}
          itemsPerPage={Number(itemsPerPage)}
          columnNames={columnNames}
          noDataTitle="No students found"
          columnBodies={(data?.entities ?? []).map(student => (
            <tr key={`${student.first_name + ' ' + student.id}`} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div
                  className="truncate max-w-[30ch]"
                  title={`${student.first_name} ${student.last_name}`}
                >
                  {`${student.first_name} ${student.last_name}`}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div className="truncate max-w-[30ch]" title={student.email}>
                  {student.email}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm group">
                <div className="truncate max-w-[30ch]">
                  <EditableMatchKeyCell
                    isOpenToManage={isOpenToManage}
                    initialValue={student.match_key}
                    studentId={student.id}
                    onSave={handleMatchKeyUpdate}
                  />
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm group">
                <div className="truncate max-w-[30ch]">
                  <EditableNicknameCell
                    isOpenToManage={isOpenToManage}
                    initialFirstName={student.nick_first_name}
                    initialLastName={student.nick_last_name}
                    studentId={student.id}
                    onSave={handleNicknameUpdate}
                  />
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                {isOpenToManage && (
                  <button
                    className="text-red-600 hover:text-red-900 focus:outline-none"
                    onClick={() => handleRemove(student.id)}
                  >
                    <img
                      src="/assets/trash.png"
                      alt="Remove"
                      className="w-5 h-5 z-5 opacity-70 hover:opacity-100 inline"
                    />
                  </button>
                )}
              </td>
            </tr>
          ))}
          updateSearchParams={updateSearchParams}
        />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        message="Are you sure you want to remove this student? This action cannot be undone."
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={confirmRemoveStudent}
      />
    </div>
  );
};

export default AddDropBig;
